import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Donate from "../components/Donate";
import InstallPWAButton from "../components/InstallPWAButton";
import InstallAppPill from "../components/InstallAppPill";
import PageMeta from "../components/PageMeta";

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1.0, ease: [0.25, 0.1, 0.25, 1] },
};

export default function Landing() {
  return (
    <main className="relative min-h-screen flex flex-col" data-testid="landing-page">
      <PageMeta
        title="The Restoration Matrix"
        description="A Christ-centered pastoral OS for discernment, the ladder of return, and quiet conversation with the Matrix. Christ-centered counsel calibrated to your actual life."
        path="/"
      />
      {/* FULL-BLEED ATMOSPHERIC BACKGROUND — DNA + sacred geometry */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        aria-hidden="true"
        style={{ transform: "translate3d(0,0,0)", willChange: "transform" }}
      >
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-cover"
          style={{
            backgroundImage: "url('/visuals/art_dna_geometry.png')",
            backgroundSize: "min(110vh, 110vw)",
            /* CSS filters force the browser to re-filter the full
               layer every scroll frame — dropped them here for
               buttery scrolling. The ink vignettes below do the
               same visual job GPU-side. */
          }}
        />
        {/* Ink vignette so the text is readable */}
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(ellipse at center, rgba(5,8,24,0.18) 0%, rgba(5,8,24,0.55) 55%, rgba(5,8,24,0.88) 100%)",
             }} />
        {/* Top + bottom ink bands for header/footer legibility */}
        <div className="absolute top-0 inset-x-0 h-32"
             style={{ background: "linear-gradient(180deg, rgba(5,8,24,0.75) 0%, transparent 100%)" }} />
        <div className="absolute bottom-0 inset-x-0 h-32"
             style={{ background: "linear-gradient(0deg, rgba(5,8,24,0.75) 0%, transparent 100%)" }} />
      </div>

      <header className="relative z-20 px-6 sm:px-12 md:px-20 pt-10 pb-3 sm:pt-12 sm:pb-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4 }}
          className="font-cinzel text-2xl sm:text-4xl lg:text-5xl text-[#FFE7A0] font-semibold tracking-[0.18em] sm:tracking-[0.22em] text-center"
          data-testid="brand-mark"
          style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
        >
          THE RESTORATION MATRIX
        </motion.div>
      </header>

      <section className="relative z-10 flex-1 flex items-center">
        <div className="w-full max-w-5xl mx-auto px-6 sm:px-12 md:px-20 pt-2 pb-6 lg:pt-4 lg:pb-8">
          <motion.div
            variants={fade}
            initial="initial"
            animate="animate"
            transition={{ duration: 1.0, delay: 0.2 }}
            className="text-center"
          >
            <div className="overline mb-10" data-testid="landing-overline">
              The Divine Blueprint · An Instrument of Restoration
            </div>

            <div
              className="mx-auto mb-10 max-w-xl rm-privacy-banner"
              data-testid="landing-privacy-disclaimer"
            >
              <span className="rm-privacy-dot" aria-hidden="true" />
              Privacy guaranteed — no record of anything is saved, and no
              account is needed to use the Matrix.
            </div>

            <div className="max-w-3xl mx-auto rm-glass-panel" data-testid="landing-hero-block">
              <h1
                className="font-serif-display text-3xl sm:text-4xl lg:text-[3.75rem] leading-[1.15] text-white tracking-tight"
                data-testid="landing-title"
                style={{ textShadow: "0 4px 60px rgba(5,8,24,0.9)" }}
              >
                You were made for{" "}
                <span className="font-medium text-[#FFE7A0]" style={{ textShadow: "0 0 80px rgba(255, 231, 160,0.5), 0 4px 60px rgba(5,8,24,0.95)" }}>
                  Love
                </span>{" "}
                to conquer everything the enemy brought into your life to destroy you.
              </h1>
            </div>

            <div className="mt-8 max-w-3xl mx-auto rm-glass-panel" data-testid="landing-os-explainer">
              <p
                className="font-body text-base sm:text-lg leading-relaxed text-white text-center"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 2px 4px rgba(5,8,24,1)" }}
              >
                The OS reads your story — your trauma, your symptoms, the situations you are walking through — and discerns the <span className="text-[#FFE7A0]">spiritual patterns moving beneath them</span>, so the Matrix can answer with Christ‑centered counsel calibrated to your actual life.
              </p>
              <p
                className="mt-5 font-body text-base sm:text-lg leading-relaxed text-white text-center"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 2px 4px rgba(5,8,24,1)" }}
                data-testid="landing-os-explainer-ladder"
              >
                Every packet is anchored in <span className="text-[#FFE7A0]">Jacob's Ladder</span> — the seven‑rung path from Exposure through Truth, Severance, Surrender, Alignment, Union, and Glory — so you see exactly where you are standing today, what the next faithful step is, and what is actively resisting your climb.
              </p>
              <p
                className="mt-5 font-body text-sm sm:text-base leading-relaxed text-[#FFE7A0] text-center italic"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 2px 4px rgba(5,8,24,1)" }}
                data-testid="landing-os-explainer-scrollhint"
              >
                Scroll down to use the Restoration Matrix, the Spiritual Warfare Engine, the Progress Engine, or chat with the OS — and explore the Community Room and the downloadable Library (Bible studies, videos, and more).
              </p>
            </div>

            <div className="mt-14 max-w-3xl mx-auto" data-testid="landing-install-pill-wrap">
              <InstallAppPill />
            </div>

            <div className="mt-4 max-w-3xl mx-auto rm-glass-panel" data-testid="landing-matrix-block">
              <h2
                className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
                data-testid="landing-matrix-title"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
              >
                The Restoration Matrix
              </h2>

              <div
                className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
                data-testid="landing-matrix-mini"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
              >
                Personal &middot; discerns your patterns and compiles a Christ‑centered Healing Packet for your walk
              </div>

              <div className="mt-8 flex items-center justify-center">
                <Link to="/intake" className="rm-btn-solid" data-testid="landing-cta-begin">
                  Begin the Intake
                </Link>
              </div>
              <div
                className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
                style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
              >
                Personal Packet ~ 6 min · single session
              </div>
            </div>
          </motion.div>

          {/* ─────────── Discernment Engine — its own section ─────────── */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center rm-glass-panel"
            data-testid="landing-discernment-block"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-8 h-px w-24 sm:w-32"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255, 231, 160,0.55) 50%, transparent 100%)" }}
            />
            <h2
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
              data-testid="landing-discernment-title"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
            >
              Spiritual Warfare Engine
            </h2>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
              data-testid="landing-discernment-mini"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Situational &middot; reads one relationship or specific tension and returns a focused Discernment Packet
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/discernment" className="rm-btn-solid" data-testid="landing-cta-discernment">
                Enter the Spiritual Warfare Engine
              </Link>
            </div>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Discernment Packet ~ 1 min · phone-friendly PDF
            </div>
          </motion.section>

          {/* Third engine — Ladder Progress */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center rm-glass-panel"
            data-testid="landing-progress-block"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-8 h-px w-24 sm:w-32"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255, 231, 160,0.55) 50%, transparent 100%)" }}
            />
            <h2
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
              data-testid="landing-progress-title"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
            >
              Ladder Progress
            </h2>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
              data-testid="landing-progress-mini"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Iterative &middot; upload a prior packet and receive a fresh reading of the rung you&rsquo;re on now
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/progress" className="rm-btn-solid" data-testid="landing-cta-progress">
                Enter the Ladder Progress Engine
              </Link>
            </div>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Progress Report ~ 40 sec · fresh ladder position
            </div>
          </motion.section>

          {/* Fourth engine — Talk with the OS (conversational chat) */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center rm-glass-panel"
            data-testid="landing-chat-block"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-8 h-px w-24 sm:w-32"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255, 231, 160,0.55) 50%, transparent 100%)" }}
            />
            <h2
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
              data-testid="landing-chat-title"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
            >
              Talk with the OS
            </h2>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
              data-testid="landing-chat-mini"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Conversational &middot; ask any question and hear the Matrix speak back in a gentle teacher&rsquo;s voice
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/chat" className="rm-btn-solid" data-testid="landing-cta-chat">
                Open the Conversation
              </Link>
            </div>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Continuous conversation &middot; no sign-up needed
            </div>
          </motion.section>

          {/* Downloadable Content / Library — public PDF library */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center rm-glass-panel"
            data-testid="landing-library-block"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-8 h-px w-24 sm:w-32"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255, 231, 160,0.55) 50%, transparent 100%)" }}
            />
            <h2
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
              data-testid="landing-library-title"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
            >
              Downloadable Content
            </h2>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
              data-testid="landing-library-mini"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Free PDFs &middot; teachings, prayers, and resources from the Matrix
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/library" className="rm-btn-solid" data-testid="landing-cta-library">
                Open the Library
              </Link>
            </div>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Open to all &middot; no sign-up needed
            </div>
          </motion.section>

          {/* Community Room (public anonymous board — not an engine, a shared space) */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center rm-glass-panel"
            data-testid="landing-community-block"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-8 h-px w-24 sm:w-32"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255, 231, 160,0.55) 50%, transparent 100%)" }}
            />
            <h2
              className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-[#FFE7A0] tracking-tight"
              data-testid="landing-community-title"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 4px 60px rgba(5,8,24,0.95)" }}
            >
              The Community Room
            </h2>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] text-center max-w-xl mx-auto leading-[1.85]"
              data-testid="landing-community-mini"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Shared &middot; a public room for testimony, prayer, and honest questions
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/community" className="rm-btn-solid" data-testid="landing-cta-community">
                Enter the Community Room
              </Link>
            </div>
            <div
              className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.28em] text-center"
              style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
            >
              Public &middot; anonymous &middot; display name only
            </div>
          </motion.section>
        </div>
      </section>

      <footer className="relative z-20 px-6 sm:px-12 md:px-20 pt-4 pb-12" data-testid="landing-footer">
        <div className="-mt-16 sm:-mt-20">
          <Donate />
        </div>

        {/* Install-as-app CTA. Self-hides on desktop / when already
           installed; renders gold native-prompt button on Android
           Chrome; renders an iOS instruction sheet on iPhone Safari. */}
        <section
          className="mt-20 max-w-2xl mx-auto px-6 text-center"
          data-testid="landing-install"
        >
          <InstallPWAButton />
        </section>

        <section
          aria-labelledby="contact-heading"
          className="mt-24 max-w-2xl mx-auto px-6 text-center rm-glass-panel"
          data-testid="landing-contact"
        >
          <div className="overline mb-4">Contact Us</div>
          <h3
            id="contact-heading"
            className="font-serif-display italic text-2xl sm:text-3xl text-white mb-4 leading-tight"
          >
            Questions, testimony, or partnership?
          </h3>
          <p className="text-white font-body font-light text-base sm:text-lg leading-relaxed mb-6">
            We read every note. If the Matrix has met you, send us your story.
            If something needs care, write us — we'll answer.
          </p>
          <a
            href="mailto:ServantoftheBride@gmail.com"
            className="inline-flex flex-col items-center gap-1 max-w-full font-cinzel tracking-[0.18em] uppercase text-[#FFE7A0] border border-[#FFE7A0]/40 px-6 py-3 hover:bg-[#FFE7A0]/10 transition-colors break-all"
            data-testid="landing-contact-email"
          >
            <span className="text-sm sm:text-base opacity-80">(MidnightCry)</span>
            <span className="text-sm sm:text-base normal-case tracking-normal">ServantoftheBride@gmail.com</span>
          </a>
        </section>
      </footer>
    </main>
  );
}
